import React, { useState } from 'react';

import { App, Button, Flex, Form, FormProps, Typography } from 'antd';

import { SearchOutlined } from '@ant-design/icons';
import { CpfField } from 'lib/CpfField';
import { UnexpectedTypeError } from 'lib/errors/UnexpectedTypeError';
import { isCpf, makeRawCpf } from 'lib/helpers/Domain.helper';
import { handleServiceError, hasServiceError } from 'lib/helpers/ServiceHelper';
import { smoothLoading } from 'lib/smoothLoading';
import { LoadedInvite } from 'pages/invite/invite.type';
import { findGuest } from 'services/Guest.service';
import { Domain } from 'types/Domain.type';
import { Guest } from 'types/Guest.type';

import { NextStep } from './useSteps';

type Props = {
    invite: LoadedInvite,
    setGuest: Utility.SetState<Guest.Model | null>,
    rawCpf: Domain.RawCpf | null,
    setRawCpf: Utility.SetState<Props['rawCpf']>,
    nextStep: NextStep,
};

type Values = { cpf: string };

export function InfoStep({ setGuest, rawCpf, setRawCpf, nextStep }: Props) {
    const [isFetching, setIsFetching] = useState(false);

    const [form] = Form.useForm<Values>();

    const app = App.useApp();

    const onFinish: FormProps<Values>['onFinish'] = async (values) => {
        if (!isCpf(values.cpf))
            throw new UnexpectedTypeError('values.cpf', typeof values.cpf, 'Domain.Cpf');

        const formRawCpf = makeRawCpf(values.cpf);

        // User was in step 2 and went back to 1,
        // but did not change the CPF, so go to step 2 again
        if (rawCpf === formRawCpf)
            return nextStep();

        setIsFetching(true);

        const response = await findGuest(formRawCpf);

        if (hasServiceError(response)) {
            setIsFetching(false);

            return handleServiceError(app, response);
        }

        setGuest(response.guest);
        setRawCpf(formRawCpf);

        await smoothLoading();

        setIsFetching(false);

        nextStep();
    };

    return (
        <Flex vertical gap="middle">
            <Typography.Paragraph style={{ margin: 0, fontWeight: 600 }}>
                Identificação do visitante
            </Typography.Paragraph>

            <Form<Values>                  
                form={form}
                onFinish={onFinish}
                initialValues={{ cpf: rawCpf ?? undefined }}
                name="infoStep"
                layout="vertical"
            >
                <CpfField rules={[{ required: true, message: 'Por favor, digite seu CPF.' }]}/>

                <Flex justify="flex-end">
                    <Button 
                        type="primary" 
                        htmlType="submit"
                        loading={isFetching}
                        icon={<SearchOutlined />}
                    >
                        Verificar CPF
                    </Button>
                </Flex>
            </Form>
        </Flex>
    );
}
